<script setup>
import ImageLazy from '~/components/ImageLazy.vue'

const props = defineProps({
  images: { type: Array, default: () => [] },
})

const main = computed(() => props.images[0])
const rest = computed(() => props.images.slice(1))

// Desktopon a galéria a szomszédos fehér blokk magasságát tölti ki: a nagy kép
// két egységnyi, a kisképek soronként egy egységnyi magasságot kapnak (a
// látványterv 2:1 aránya), így 3 és 5 képnél is leér a blokk aljáig.
const restRows = computed(() => Math.ceil(rest.value.length / 2))
</script>

<template>
  <div v-if="images.length" class="flex flex-col gap-4 lg:h-full">
    <!-- `object-cover`: a galériában álló és fekvő képek is vannak (pl.
         pressure-vessel/2.webp álló), egységes rács kell. Mobilon fix arány,
         desktopon a rendelkezésre álló magasság osztódik szét. -->
    <ImageLazy
      v-if="main"
      v-reveal
      :src="'/' + main.src"
      :alt="main.alt"
      aspect="16/10"
      class="reveal reveal-zoom gallery-tile gallery-main group aspect-[16/10] overflow-hidden rounded-xl"
      img-class="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
    />
    <div
      v-if="rest.length"
      class="gallery-rest grid gap-4 auto-rows-fr"
      :class="rest.length === 1 ? 'grid-cols-1' : 'grid-cols-2'"
      :style="{ '--rest-rows': restRows }"
    >
      <ImageLazy
        v-for="(image, index) in rest"
        :key="image.src"
        v-reveal="index + 1"
        :src="'/' + image.src"
        :alt="image.alt"
        aspect="4/3"
        class="reveal reveal-zoom gallery-tile group aspect-[4/3] overflow-hidden rounded-xl"
        img-class="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
      />
    </div>
  </div>
</template>

<style scoped>
/* A képek fölé kerülő halvány sötét fátyol hoverre eltűnik — így a rács
   nyugodtabb, a kiemelt kép pedig „életre kel”. */
.gallery-tile {
  position: relative;
  /* Rács-/flexelemként a `min-width: auto` a kép saját szélességét venné alsó
     korlátnak, és a csempék kilógnának az oszlopból (a rés is elfogyna). */
  min-width: 0;
  min-height: 0;
  /* Az opacity/transform is itt: ez a szabály felülírja a v-reveal globális
     transition-jét (assets/main.css). */
  transition: box-shadow .35s ease, opacity .7s ease, transform .7s cubic-bezier(.22, .61, .36, 1);
}
.gallery-tile::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(to top, rgba(27, 42, 74, .28), transparent 55%);
  opacity: 1;
  transition: opacity .45s ease;
}
.gallery-tile:hover::after {
  opacity: .25;
}
.gallery-tile:hover {
  box-shadow: 0 12px 28px rgba(0, 0, 0, .18);
}

@media (min-width: 1024px) {
  /* Desktopon nem a képarány, hanem a kiosztott magasság határozza meg a
     csempét — a scoped selector felülírja a Tailwind `aspect-[...]` osztályt. */
  .gallery-tile {
    aspect-ratio: auto;
  }
  .gallery-main {
    flex: 2 1 0%;
    min-height: 0;
  }
  .gallery-rest {
    flex: var(--rest-rows, 1) 1 0%;
    min-height: 0;
  }
  /* A kisképek a rácssoruk teljes magasságát kitöltik; a nagy képnek a flex
     adja a magasságot, ott a height:100% elrontaná a felosztást. */
  .gallery-rest .gallery-tile {
    height: 100%;
  }
}
</style>
